<template>
  <div class="note-item d-flex pb-4">
    <user-avatar-badge
      :is-system="isSystemNote"
      :name="authorName"
      margin-class="mr-3"
      system-aria-label="System note"
      class="note-item__marker flex-shrink-0"
    />

    <div class="flex-grow-1">
      <div class="d-flex align-items-start justify-content-between">
        <div class="d-flex flex-wrap align-items-center pr-3">
          <span class="note-item__title font-w600 mr-1">{{ authorName }}</span>
          <b-badge
            v-if="isTechnician"
            variant="secondary"
            class="status-badge font-size-sm mr-1"
          >
            {{ value.user.category | humanizedFormat | capitalizeFormat }}
          </b-badge>
        </div>

        <div class="d-flex align-items-center flex-shrink-0">
          <div
            class="text-muted font-size-sm text-nowrap"
            v-b-tooltip.hover
            :title="value.created_at | dateTimeFormat"
          >
            {{ value.created_at | dateOrTimeFormat }}
          </div>
          <b-dropdown
            variant="link"
            size="sm"
            no-caret
            right
            class="ml-1"
          >
            <template #button-content>
              <i class="fa fa-ellipsis-v" />
            </template>
            <b-dropdown-item
              @click="() => isPinned ? unpinNote() : pinNote()"
            >
              {{ isPinned ? 'Unpin Note' : 'Pin Note' }}
            </b-dropdown-item>
            <b-dropdown-item
              @click="() => destroyNote()"
            >
              Delete Note
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="d-flex align-items-start mt-1">
        <div class="note-item__content text-prewrap flex-grow-1 overflow-hidden">
          {{ value.content }}
        </div>
        <span
          v-if="isExternal"
          class="text-muted font-w600 font-size-sm text-nowrap ml-2 flex-shrink-0"
          v-b-tooltip.hover
          :title="`This note is visible to the ${$store.getters.translate('client', 'Client')}`"
        >
          <i class="fa fa-eye mr-1" />
          External
        </span>
      </div>

      <timeline-item-reference
        v-if="showReference"
        :reference-type="value.reference_type"
        :reference="value.reference"
        class="d-inline-block mt-1"
      />
    </div>
  </div>
</template>

<script>
import { isNil as _isNil } from 'lodash';
import { DateTime } from 'luxon';
import TimelineItemReference from './TimelineItemReference.vue';
import UserAvatarBadge from '@/components/shared/users/UserAvatarBadge.vue';
import { USER_TECHNICIAN_CATEGORY, USER_SUB_TECHNICIAN_CATEGORY } from '@/constants/users';

export default {
  components: {
    TimelineItemReference,
    UserAvatarBadge,
  },
  name: 'NotesTimelineItem',
  props: {
    value: {
      type: Object,
      required: true,
    },
    showReference: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    destroyNote() {
      this.$swal({
        text: 'Once it\'s deleted, its gone forever!',
        preConfirm: () => this.$notesAPI.updateNote(this.value.id, { deleted_at: DateTime.now().toISODate() }),
      }).then((result) => {
        if (result.value) {
          this.$emit('on-destroy');
        }
        result.timer;
      });
    },
    pinNote() {
      this.$swal({
        text: 'Are you sure you want to pin this note?',
        preConfirm: () => this.$notesAPI.updateNote(this.value.id, { pinned_at: DateTime.now().toISO() }),
      }).then((result) => {
        if (result.value) {
          this.$emit('on-pinned', result.value);
        }
        result.timer;
      });
    },
    unpinNote() {
      this.$swal({
        text: 'Are you sure you want to unpin this note?',
        preConfirm: () => this.$notesAPI.updateNote(this.value.id, { pinned_at: null }),
      }).then((result) => {
        if (result.value) {
          this.$emit('on-unpinned', result.value);
        }
        result.timer;
      });
    },
  },
  computed: {
    isSystemNote() {
      return !this.value.user;
    },
    authorName() {
      return this.value.user?.name || 'System';
    },
    isPinned() {
      return !_isNil(this.value.pinned_at);
    },
    isExternal() {
      return !this.value.internal;
    },
    isTechnician() {
      return this.value.user && [USER_TECHNICIAN_CATEGORY, USER_SUB_TECHNICIAN_CATEGORY].includes(this.value.user.category);
    },
  },
};
</script>

<style scoped>
/* Avatar doubles as the timeline marker; border/outline let the trail pass cleanly behind it. */
.note-item__marker {
  position: relative;
  z-index: 1;
  border: 2px solid #FFFFFF;
  outline: 1px solid #e9ecef;
}

.note-item__title {
  color: #343A40;
  line-height: 1.35;
}

.note-item__content {
  color: #343A40;
  line-height: 1.35;
}
</style>
